/**
 * Vibração do /controle.
 *
 * O operador fica com o celular na mão e o olho no salão, não na tela. Uma
 * vibração curta confirma "a bola saiu" sem ele precisar olhar — e padrões
 * diferentes distinguem bingo confirmado de não confirmado, que é a
 * informação que ele precisa passar adiante na hora.
 *
 * Três condições para vibrar, todas respeitadas:
 *
 * 1. **suporte** — `navigator.vibrate` não existe no iOS nem no desktop;
 * 2. **preferência do sistema** — `prefers-reduced-motion` é um pedido
 *    explícito de menos estímulo, e vibração é estímulo;
 * 3. **ajuste do evento** — quem opera pode desligar.
 */

export const VIBRATION_PATTERNS = {
  /** Bola revelada: um toque curto, quase um "tic". */
  ball: [18],
  /** Bingo confirmado: dois toques curtos e um longo — comemoração. */
  bingoConfirmed: [40, 60, 40, 60, 140],
  /** Não confirmado: um toque longo e grave, diferente de qualquer outro. */
  bingoRejected: [220],
  /** Erro de comando: dois toques iguais. */
  error: [60, 80, 60],
} as const;

export type VibrationKind = keyof typeof VIBRATION_PATTERNS;

export function vibrationSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

/** O sistema pediu menos movimento? */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Decide se deve vibrar. Separado de `vibrate` para poder ser testado sem
 * um navegador — é aqui que mora a regra, não na chamada.
 */
export function shouldVibrate(options: {
  enabled: boolean;
  supported?: boolean;
  reducedMotion?: boolean;
}): boolean {
  if (!options.enabled) return false;
  if (!(options.supported ?? vibrationSupported())) return false;
  if (options.reducedMotion ?? prefersReducedMotion()) return false;
  return true;
}

export function vibrate(kind: VibrationKind, enabled: boolean): void {
  if (!shouldVibrate({ enabled })) return;

  try {
    navigator.vibrate(VIBRATION_PATTERNS[kind] as unknown as number[]);
  } catch {
    // Vibração é enfeite: falhar aqui não pode derrubar o controle.
  }
}
